/**
 * Unified platform checkout review — itemized breakdown before Razorpay.
 * User must review subtotal, discounts, tax, fees, and final payable before paying.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Lock, Tag } from "lucide-react";
import { toast } from "sonner";
import {
  RazorpayCheckoutButton,
  RazorpaySubscriptionCheckoutButton,
} from "@/components/billing/RazorpayCheckout";
import {
  createCheckoutOrder,
  fetchCheckoutQuote,
  formatCheckoutInr,
  type CheckoutQuoteRequest,
  type PlatformCheckoutQuote,
} from "@/lib/platform-payments/checkout-quote";
import { dataProvider as supabase } from "@/lib/providers/data-provider";

export type PlatformCheckoutReviewProps = {
  request: CheckoutQuoteRequest;
  title?: string;
  subtitle?: string;
  testMode?: boolean;
  allowCoupon?: boolean;
  onSuccess?: (verifyResult?: Record<string, unknown>) => void;
  onFailure?: (reason?: string) => void;
  onCancel?: () => void;
};

export function PlatformCheckoutReview({
  request,
  title,
  subtitle,
  testMode,
  allowCoupon = true,
  onSuccess,
  onFailure,
  onCancel,
}: PlatformCheckoutReviewProps) {
  const [quote, setQuote] = useState<PlatformCheckoutQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [couponInput, setCouponInput] = useState(request.discount_code ?? "");
  const [appliedCode, setAppliedCode] = useState(request.discount_code ?? "");
  const [orderSession, setOrderSession] = useState<{
    orderId?: string;
    subscriptionId?: string;
    checkoutMode?: "order" | "subscription";
    keyId: string;
    internalPaymentId?: string;
    amountPaise: number;
    planCode?: string;
    billingPeriod?: string;
  } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);

  const loadQuote = useCallback(
    async (discountCode?: string) => {
      setLoading(true);
      const result = await fetchCheckoutQuote({
        ...request,
        discount_code: discountCode ?? appliedCode,
      });
      setLoading(false);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setQuote(result.quote);
      setOrderSession(null);
      setReviewConfirmed(false);
    },
    [request, appliedCode],
  );

  useEffect(() => {
    void loadQuote();
  }, [loadQuote]);

  async function applyCoupon() {
    const code = couponInput.trim();
    if (!code) return;
    setAppliedCode(code);
    await loadQuote(code);
  }

  async function handleConfirmReview() {
    if (!quote) return;
    setConfirming(true);
    const result = await createCheckoutOrder({
      ...request,
      discount_code: appliedCode || undefined,
    });
    setConfirming(false);
    if (!result.ok || !result.keyId || (!result.orderId && !result.subscriptionId)) {
      toast.error(result.error ?? "Could not start payment");
      onFailure?.(result.error);
      return;
    }
    setOrderSession({
      orderId: result.orderId,
      subscriptionId: result.subscriptionId,
      checkoutMode: result.checkoutMode,
      keyId: result.keyId!,
      internalPaymentId: result.internalPaymentId,
      amountPaise: result.amountPaise ?? quote.payable_paise,
      planCode: result.planCode ?? quote.plan_code,
      billingPeriod: result.billingPeriod ?? quote.billing_period,
    });
    setReviewConfirmed(true);
  }

  if (loading && !quote) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Preparing checkout…
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        Could not load checkout. Please try again.
        {onCancel ? (
          <Button variant="ghost" size="sm" className="mt-2" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    );
  }

  const displayTitle = title ?? quote.line_items[0]?.label ?? "Platform checkout";

  return (
    <div
      className="rounded-xl border border-gold/25 bg-gradient-to-br from-gold/5 via-card to-card p-5 space-y-4 shadow-sm"
      data-testid="platform-checkout-review"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
            <Lock className="h-3 w-3" /> Review before payment
          </p>
          <h3 className="text-base font-semibold text-foreground mt-0.5">{displayTitle}</h3>
          {subtitle ? <p className="text-xs text-muted-foreground mt-1">{subtitle}</p> : null}
        </div>
        {quote.gst_enabled ? (
          <Badge variant="outline" className="text-[10px]">GST applicable</Badge>
        ) : null}
      </div>

      {/* Line items */}
      <div className="rounded-lg border border-border/60 bg-background/50 divide-y divide-border/50 text-sm">
        {quote.line_items.map((item) => (
          <div key={item.label} className="flex justify-between gap-3 px-3 py-2">
            <span className="text-muted-foreground">{item.label}</span>
            <span className="font-mono font-medium">{formatCheckoutInr(item.amount_paise)}</span>
          </div>
        ))}
        <div className="flex justify-between gap-3 px-3 py-2">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-mono">{formatCheckoutInr(quote.subtotal_paise)}</span>
        </div>
        {quote.annual_discount_paise > 0 ? (
          <div className="flex justify-between gap-3 px-3 py-2 text-emerald-600">
            <span>Annual billing discount</span>
            <span className="font-mono">−{formatCheckoutInr(quote.annual_discount_paise)}</span>
          </div>
        ) : null}
        {quote.discount_paise > 0 ? (
          <div className="flex justify-between gap-3 px-3 py-2 text-emerald-600">
            <span className="flex items-center gap-1">
              <Tag className="h-3 w-3" />
              Coupon {quote.discount_code}
              {quote.discount_label ? ` (${quote.discount_label})` : ""}
            </span>
            <span className="font-mono">−{formatCheckoutInr(quote.discount_paise)}</span>
          </div>
        ) : null}
        {quote.credit_adjustment_paise > 0 ? (
          <div className="flex justify-between gap-3 px-3 py-2 text-emerald-600">
            <span>Credit adjustment</span>
            <span className="font-mono">−{formatCheckoutInr(quote.credit_adjustment_paise)}</span>
          </div>
        ) : null}
        {quote.gst_enabled ? (
          <div className="flex justify-between gap-3 px-3 py-2">
            <span className="text-muted-foreground">Amount before tax</span>
            <span className="font-mono">{formatCheckoutInr(quote.pre_tax_paise)}</span>
          </div>
        ) : null}
        {quote.fees_paise > 0 ? (
          <div className="flex justify-between gap-3 px-3 py-2">
            <span className="text-muted-foreground">Fees</span>
            <span className="font-mono">{formatCheckoutInr(quote.fees_paise)}</span>
          </div>
        ) : null}
        {quote.gst_enabled && quote.tax.gst_enabled ? (
          <div className="px-3 py-2 space-y-1 bg-muted/20">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Tax / GST ({quote.tax.gst_rate_pct}%)
            </p>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Taxable value</span>
              <span className="font-mono">{formatCheckoutInr(quote.tax.taxable_paise)}</span>
            </div>
            {quote.tax.is_inter_state ? (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">IGST</span>
                <span className="font-mono">{formatCheckoutInr(quote.tax.igst_paise)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">CGST</span>
                  <span className="font-mono">{formatCheckoutInr(quote.tax.cgst_paise)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">SGST</span>
                  <span className="font-mono">{formatCheckoutInr(quote.tax.sgst_paise)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-xs font-medium pt-1 border-t border-border/40">
              <span className="text-muted-foreground">Total tax</span>
              <span className="font-mono">{formatCheckoutInr(quote.tax.total_tax_paise)}</span>
            </div>
          </div>
        ) : null}
        <div className="flex justify-between gap-3 px-3 py-3 font-semibold text-base">
          <span>Amount payable</span>
          <span className="font-mono text-gold text-xl">{formatCheckoutInr(quote.payable_paise)}</span>
        </div>
      </div>

      {allowCoupon && !reviewConfirmed ? (
        <div className="flex gap-2">
          <Input
            placeholder="Discount / coupon code"
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
            className="text-sm font-mono uppercase"
          />
          <Button variant="outline" size="sm" onClick={() => void applyCoupon()} disabled={loading}>
            Apply
          </Button>
        </div>
      ) : null}

      {!reviewConfirmed ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground text-center">
            Review the breakdown above. You will be taken to Razorpay only after confirming.
          </p>
          <Button
            className="w-full"
            onClick={() => void handleConfirmReview()}
            disabled={confirming || loading}
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Confirm &amp; proceed to payment
          </Button>
          {onCancel ? (
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      ) : orderSession ? (
        <div className="space-y-2">
          <p className="text-xs text-center text-muted-foreground">
            {orderSession.checkoutMode === "subscription"
              ? "Breakdown confirmed. Authorize your recurring subscription via Razorpay."
              : "Breakdown confirmed. Complete payment securely via Razorpay."}
          </p>
          {orderSession.checkoutMode === "subscription" && orderSession.subscriptionId ? (
            <RazorpaySubscriptionCheckoutButton
              subscriptionId={orderSession.subscriptionId}
              keyId={orderSession.keyId}
              label={`Subscribe ${formatCheckoutInr(orderSession.amountPaise)}/${orderSession.billingPeriod === "annual" ? "yr" : "mo"}`}
              onSuccess={async () => {
                try {
                  let tenantId = request.tenant_id;
                  if (!tenantId) {
                    const { data: sessionData } = await supabase.auth.getSession();
                    const authId = sessionData.session?.user?.id;
                    if (authId) {
                      const { data: profile } = await supabase
                        .from("user_profiles" as never)
                        .select("firm_id")
                        .eq("auth_id", authId)
                        .maybeSingle();
                      tenantId = (profile as { firm_id?: string } | null)?.firm_id;
                    }
                  }
                  if (tenantId && orderSession.subscriptionId) {
                    await fetch("/api/payments/confirm-plan-subscription.php", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        organization_id: tenantId,
                        subscription_id: orderSession.subscriptionId,
                        plan_code: orderSession.planCode,
                        billing_period: orderSession.billingPeriod,
                      }),
                    });
                  }
                } catch {
                  /* webhook may still fulfill */
                }
                onSuccess?.();
              }}
              onFailure={onFailure}
            />
          ) : (
            <RazorpayCheckoutButton
              orderId={orderSession.orderId!}
              amountPaise={orderSession.amountPaise}
              keyId={orderSession.keyId}
              internalPaymentId={orderSession.internalPaymentId}
              label={`Pay ${formatCheckoutInr(orderSession.amountPaise)}`}
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            onClick={() => setReviewConfirmed(false)}
          >
            Back to review
          </Button>
        </div>
      ) : null}

      {testMode ? (
        <p className="text-[10px] text-center text-amber-600/90">
          Demo test mode — card 4111 1111 1111 1111 · any future expiry · any CVV
        </p>
      ) : null}
    </div>
  );
}
